import { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRankingData } from '../../hooks/useRankingData';
import { BottomNav } from '../../components/feature/BottomNav';
import type { ArtistRelativeScore } from '../../types/ranking';

type TabType = 'artists' | 'nodes' | 'seekers';
type ArtistSortType = 'total' | 'quality' | 'breadth' | 'activity' | 'growth' | 'core_fans';

const PREFECTURES = [
  '全国', '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県',
  '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県',
  '奈良県', '和歌山県', '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県', '福岡県', '佐賀県', '長崎県',
  '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県',
];

// ツールチップコンポーネント
interface TooltipProps {
  content: string;
  subContent?: string;
  children: React.ReactNode;
  position?: 'top' | 'bottom';
}

function Tooltip({ content, subContent, children, position = 'top' }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  return (
    <div
      className="relative inline-flex items-center"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {visible && (
        <div
          className={`absolute z-50 left-1/2 -translate-x-1/2 ${
            position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
          } w-max max-w-[200px] pointer-events-none`}
        >
          <div className="bg-[#0d0d0d] border border-neon-cyan/40 rounded-lg px-3 py-2 shadow-[0_0_16px_rgba(0,217,255,0.2)]">
            <p className="text-xs font-bold text-neon-cyan tracking-wider whitespace-nowrap">{content}</p>
            {subContent && (
              <p className="text-[10px] text-gray-400 font-mono mt-0.5 leading-relaxed">{subContent}</p>
            )}
          </div>
          {/* 矢印 */}
          <div
            className={`absolute left-1/2 -translate-x-1/2 w-0 h-0 ${
              position === 'top'
                ? 'top-full border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-neon-cyan/40'
                : 'bottom-full border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-neon-cyan/40'
            }`}
          ></div>
        </div>
      )}
    </div>
  );
}

export default function RankingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialTab = (location.state as { tab?: TabType } | null)?.tab ?? 'artists';
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [selectedPrefecture, setSelectedPrefecture] = useState('奈良県');
  const [artistSort, setArtistSort] = useState<ArtistSortType>('total');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  const prefectureParam = selectedPrefecture === '全国' ? undefined : selectedPrefecture;
  const { artists, nodes, seekers, loading, error } = useRankingData(prefectureParam);

  const sortOptions: {
    value: ArtistSortType;
    label: string;
    sublabel: string;
    icon: string;
    tooltip: string;
    tooltipSub: string;
  }[] = [
    {
      value: 'total',
      label: '総合力',
      sublabel: 'Total',
      icon: 'ri-trophy-line',
      tooltip: '総合スコア (0〜100pt)',
      tooltipSub: '5つの指標を均等に合算した\n総合的な強さを示すスコア',
    },
    {
      value: 'quality',
      label: '熱狂の質',
      sublabel: 'Quality',
      icon: 'ri-fire-line',
      tooltip: '熱狂の質 (0〜20pt)',
      tooltipSub: '投げ銭・サポート収益の合計額\nをもとに算出。深い支持の度合い',
    },
    {
      value: 'breadth',
      label: '熱狂の幅',
      sublabel: 'Breadth',
      icon: 'ri-radar-line',
      tooltip: '熱狂の幅 (0〜20pt)',
      tooltipSub: 'ユニークサポーター数をもとに算出\n幅広いファン層の広がりを示す',
    },
    {
      value: 'activity',
      label: 'イベント開催数',
      sublabel: 'Activity',
      icon: 'ri-calendar-event-line',
      tooltip: 'アクティビティ (0〜20pt)',
      tooltipSub: '今月のイベント開催数をもとに算出\n活動量・現場への積極性を示す',
    },
    {
      value: 'growth',
      label: '成長率',
      sublabel: 'Growth',
      icon: 'ri-line-chart-line',
      tooltip: '成長率 (0〜20pt)',
      tooltipSub: 'SNSフォロワーの前月比成長率\nをもとに算出。勢いと注目度を示す',
    },
    {
      value: 'core_fans',
      label: 'コアファン',
      sublabel: 'Core Fans',
      icon: 'ri-heart-3-line',
      tooltip: 'コアファン数 (0〜20pt)',
      tooltipSub: '複数回支援した熱狂的ファンの数\nをもとに算出。固定ファン層の厚さ',
    },
  ];

  // スコアバー用の指標定義（ツールチップ付き）
  const scoreBars: {
    label: string;
    key: keyof ArtistRelativeScore;
    color: string;
    tooltip: string;
    tooltipSub: string;
  }[] = [
    {
      label: 'BRD',
      key: 'breadth_score',
      color: 'bg-neon-cyan',
      tooltip: '熱狂の幅',
      tooltipSub: 'ユニークサポーター数\nから算出 (0〜20pt)',
    },
    {
      label: 'QLT',
      key: 'quality_score',
      color: 'bg-neon-purple',
      tooltip: '熱狂の質',
      tooltipSub: '収益・投げ銭合計額\nから算出 (0〜20pt)',
    },
    {
      label: 'ACT',
      key: 'activity_score',
      color: 'bg-neon-pink',
      tooltip: 'アクティビティ',
      tooltipSub: 'イベント開催数\nから算出 (0〜20pt)',
    },
    {
      label: 'CORE',
      key: 'core_fan_score',
      color: 'bg-yellow-400',
      tooltip: 'コアファン',
      tooltipSub: '複数回支援ファン数\nから算出 (0〜20pt)',
    },
    {
      label: 'GRW',
      key: 'growth_score',
      color: 'bg-green-400',
      tooltip: '成長率',
      tooltipSub: 'SNS前月比成長率\nから算出 (0〜20pt)',
    },
  ];

  const sortKeyMap: Record<ArtistSortType, keyof ArtistRelativeScore> = {
    total:     'total_score',
    quality:   'quality_score',
    breadth:   'breadth_score',
    activity:  'activity_score',
    growth:    'growth_score',
    core_fans: 'core_fan_score',
  };

  const getSortedArtists = () =>
    [...artists].sort((a, b) => {
      const key = sortKeyMap[artistSort];
      return ((b[key] as number) || 0) - ((a[key] as number) || 0);
    });

  const getRankColor = (rank: number) => {
    if (rank === 1) return 'from-yellow-400 to-yellow-600';
    if (rank === 2) return 'from-gray-300 to-gray-500';
    if (rank === 3) return 'from-orange-400 to-orange-600';
    return 'from-gray-700 to-gray-800';
  };

  const getRankGlow = (rank: number) => {
    if (rank === 1) return 'shadow-[0_0_20px_rgba(250,204,21,0.6)]';
    if (rank === 2) return 'shadow-[0_0_20px_rgba(209,213,219,0.5)]';
    if (rank === 3) return 'shadow-[0_0_20px_rgba(251,146,60,0.5)]';
    return '';
  };

  const getRankBorder = (rank: number) => {
    if (rank === 1) return 'border-yellow-400';
    if (rank === 2) return 'border-gray-400';
    if (rank === 3) return 'border-orange-400';
    return 'border-white/10';
  };

  const currentSort = sortOptions.find(o => o.value === artistSort)!;

  const SkeletonCard = () => (
    <div className="bg-white/5 border border-white/10 rounded-lg p-4 animate-pulse">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-white/10 flex-shrink-0"></div>
        <div className="w-14 h-14 rounded-full bg-white/10 flex-shrink-0"></div>
        <div className="flex-1 space-y-2">
          <div className="h-5 bg-white/10 rounded w-3/4"></div>
          <div className="h-3 bg-white/10 rounded w-1/2"></div>
        </div>
        <div className="w-16 h-8 bg-white/10 rounded flex-shrink-0"></div>
      </div>
    </div>
  );

  const EmptyState = ({ message }: { message: string }) => (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="w-24 h-24 rounded-full bg-white/5 flex items-center justify-center mb-4">
        <i className="ri-inbox-line text-4xl text-gray-600"></i>
      </div>
      <p className="text-gray-400 text-center font-mono text-sm tracking-wider">{message}</p>
    </div>
  );

  // 集計期間・更新日時の計算
  const infoBanner = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const firstDay = `${year}/${String(month).padStart(2, '0')}/01`;
    const lastDay = new Date(year, month, 0).getDate();
    const endDay = `${year}/${String(month).padStart(2, '0')}/${lastDay}`;
    const updatedAt = now.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
    return { firstDay, endDay, updatedAt, month };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white pb-24">
      {/* ヘッダー */}
      <div className="sticky top-0 z-30 bg-black/95 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold tracking-wider">
              <span className="bg-gradient-to-r from-neon-cyan via-neon-purple to-neon-pink bg-clip-text text-transparent">
                RANKING
              </span>
            </h1>
            <div className="flex items-center gap-2">
              {/* ⓘ インフォボタン */}
              <div className="relative">
                <button
                  onClick={() => setInfoOpen(!infoOpen)}
                  className={`w-8 h-8 flex items-center justify-center rounded-full border transition-all cursor-pointer ${
                    infoOpen
                      ? 'bg-neon-cyan/20 border-neon-cyan text-neon-cyan shadow-[0_0_10px_rgba(0,217,255,0.4)]'
                      : 'bg-white/5 border-white/20 text-gray-400 hover:border-neon-cyan/50 hover:text-neon-cyan'
                  }`}
                  aria-label="集計情報・指標説明"
                >
                  <i className="ri-information-line text-sm"></i>
                </button>

                {/* ポップオーバー */}
                {infoOpen && (
                  <>
                    {/* オーバーレイ（外クリックで閉じる） */}
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setInfoOpen(false)}
                    ></div>

                    <div className="absolute right-0 top-full mt-2 z-50 w-72 bg-[#0a0a0a] border border-neon-cyan/30 rounded-xl shadow-[0_0_32px_rgba(0,217,255,0.15)] overflow-hidden">
                      {/* 集計期間 */}
                      <div className="px-4 pt-4 pb-3 border-b border-white/8">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-5 h-5 flex items-center justify-center rounded-full bg-neon-cyan/10">
                            <i className="ri-calendar-2-line text-neon-cyan text-xs"></i>
                          </div>
                          <span className="text-[10px] font-mono text-gray-500 tracking-widest uppercase">Aggregation Period</span>
                        </div>
                        <div className="flex items-center gap-2 pl-1">
                          <span className="text-xs font-mono text-neon-cyan tracking-widest">
                            {infoBanner.firstDay} – {infoBanner.endDay}
                          </span>
                          <span className="text-[10px] font-mono text-gray-600 border border-white/10 rounded px-1.5 py-0.5 tracking-widest">
                            {infoBanner.month}月度
                          </span>
                        </div>
                      </div>

                      {/* 最終更新 */}
                      <div className="px-4 py-3 border-b border-white/8">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-5 h-5 flex items-center justify-center rounded-full bg-white/5 relative">
                            <span className="absolute inline-flex w-1.5 h-1.5 rounded-full bg-neon-cyan opacity-60 animate-ping"></span>
                            <span className="relative inline-flex w-1 h-1 rounded-full bg-neon-cyan"></span>
                          </div>
                          <span className="text-[10px] font-mono text-gray-500 tracking-widest uppercase">Latest Update</span>
                        </div>
                        <span className="text-xs font-mono text-gray-300 tracking-wider pl-1">{infoBanner.updatedAt}</span>
                      </div>

                      {/* スコア指標 */}
                      <div className="px-4 py-3">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-5 h-5 flex items-center justify-center rounded-full bg-neon-purple/10">
                            <i className="ri-bar-chart-2-line text-neon-purple text-xs"></i>
                          </div>
                          <span className="text-[10px] font-mono text-gray-500 tracking-widest uppercase">Score Metrics</span>
                        </div>
                        <div className="space-y-2">
                          {scoreBars.map(bar => (
                            <div key={bar.label} className="flex items-start gap-2.5">
                              <div className={`w-2 h-2 rounded-full ${bar.color} mt-1 flex-shrink-0`}></div>
                              <div className="flex-1">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[10px] font-mono font-bold text-gray-300 tracking-widest">{bar.label}</span>
                                  <span className="text-[10px] font-mono text-gray-500">—</span>
                                  <span className="text-[10px] font-mono text-gray-400">{bar.tooltip}</span>
                                </div>
                                <p className="text-[10px] text-gray-600 font-mono mt-0.5 leading-relaxed">
                                  {bar.tooltipSub.replace(/\n/g, ' ')}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* NODESタブ時の追記 */}
                      {activeTab === 'nodes' && (
                        <div className="px-4 pb-3 border-t border-white/8 pt-3">
                          <div className="flex items-center gap-2">
                            <i className="ri-time-line text-neon-pink text-xs"></i>
                            <span className="text-[10px] font-mono text-neon-pink tracking-widest">WINDOW: 直近24時間</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              <i className="ri-map-pin-line text-neon-cyan"></i>
              <select
                value={selectedPrefecture}
                onChange={(e) => setSelectedPrefecture(e.target.value)}
                className="bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-neon-cyan transition-colors cursor-pointer"
              >
                {PREFECTURES.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* タブ切り替え */}
      <div className="sticky top-[73px] z-20 bg-black/95 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-4">
          <div className="flex gap-1 py-3">
            {[
              { key: 'artists', label: 'ARTISTS', icon: 'ri-user-star-line' },
              { key: 'nodes',   label: 'NODES',   icon: 'ri-store-3-line' },
              { key: 'seekers', label: 'SEEKERS', icon: 'ri-team-line' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as TabType)}
                className={`flex-1 py-3 px-4 rounded-lg font-bold text-sm tracking-wider transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === tab.key
                    ? 'bg-gradient-to-r from-neon-cyan to-neon-purple text-white shadow-[0_0_20px_rgba(0,217,255,0.4)]'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                <i className={`${tab.icon} mr-2`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* エラー表示 */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <i className="ri-error-warning-line text-red-500"></i>
              <span className="text-red-400 text-sm font-mono">{error}</span>
            </div>
          </div>
        )}

        {/* ローディング */}
        {loading && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-neon-cyan animate-ping"></div>
              <span className="text-xs font-mono text-neon-cyan tracking-[0.3em] animate-pulse">
                LOADING DATA...
              </span>
            </div>
            {[...Array(5)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {/* ARTISTS タブ */}
        {!loading && activeTab === 'artists' && (
          <div className="space-y-6">
            {/* ソートドロップダウン */}
            <div className="relative">
              <button
                onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                className="flex items-center gap-3 bg-white/5 border border-white/20 hover:border-neon-cyan/60 rounded-lg px-4 py-3 transition-all cursor-pointer group w-full"
              >
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className={`${currentSort.icon} text-neon-cyan text-lg`}></i>
                </div>
                <div className="flex-1 text-left">
                  <span className="text-xs text-gray-500 tracking-widest block font-mono">SORT BY</span>
                  <span className="text-sm font-bold tracking-wider text-white">
                    {currentSort.label}
                    <span className="text-gray-400 font-normal ml-2 text-xs font-mono">{currentSort.sublabel}</span>
                  </span>
                </div>
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className={`ri-arrow-${sortDropdownOpen ? 'up' : 'down'}-s-line text-gray-400 group-hover:text-neon-cyan transition-colors text-lg`}></i>
                </div>
              </button>

              {sortDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[#0d0d0d] border border-white/20 rounded-lg overflow-hidden z-40 shadow-[0_8px_32px_rgba(0,0,0,0.8)]">
                  {sortOptions.map((option, idx) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setArtistSort(option.value);
                        setSortDropdownOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 transition-all cursor-pointer text-left ${
                        artistSort === option.value
                          ? 'bg-neon-cyan/10 border-l-2 border-neon-cyan'
                          : 'hover:bg-white/5 border-l-2 border-transparent'
                      } ${idx !== 0 ? 'border-t border-white/5' : ''}`}
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5">
                        <i className={`${option.icon} ${artistSort === option.value ? 'text-neon-cyan' : 'text-gray-400'} text-base`}></i>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-bold tracking-wider ${artistSort === option.value ? 'text-neon-cyan' : 'text-white'}`}>
                            {option.label}
                          </span>
                          <span className="text-xs text-gray-400 font-mono">{option.sublabel}</span>
                        </div>
                        <p className="text-[10px] text-gray-600 font-mono mt-0.5 leading-relaxed">
                          {option.tooltipSub.replace(/\n/g, ' ')}
                        </p>
                      </div>
                      {artistSort === option.value && (
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-check-line text-neon-cyan text-sm"></i>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ランキングリスト */}
            {artists.length === 0 ? (
              <EmptyState message="NO DATA FOUND" />
            ) : (
              <div className="space-y-3">
                {getSortedArtists().map((artist, index) => {
                  const rank = index + 1;
                  const scoreValue = (artist[sortKeyMap[artistSort]] as number) || 0;

                  return (
                    <div
                      key={artist.artist_id}
                      onClick={() => navigate(`/artist/${artist.artist_id}`)}
                      className={`bg-white/5 border ${getRankBorder(rank)} rounded-lg p-4 hover:bg-white/10 transition-all ${getRankGlow(rank)} cursor-pointer`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${getRankColor(rank)} flex items-center justify-center flex-shrink-0`}>
                          <span className="text-2xl font-mono font-bold text-white">{rank}</span>
                        </div>
                        <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white/20 flex-shrink-0">
                          {artist.avatar_url ? (
                            <img src={artist.avatar_url} alt={artist.artist_name} className="w-full h-full object-cover object-top" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-neon-cyan to-neon-purple flex items-center justify-center">
                              <i className="ri-user-star-line text-2xl text-white"></i>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold tracking-wider truncate">{artist.artist_name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <i className={`${currentSort.icon} text-neon-cyan text-xs`}></i>
                            <span className="text-xs text-gray-400 font-mono">{currentSort.sublabel}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="text-right">
                            <div className="text-2xl font-mono font-bold bg-gradient-to-r from-neon-cyan to-neon-purple bg-clip-text text-transparent">
                              {scoreValue.toFixed(1)}
                            </div>
                            <div className="text-xs text-gray-400 tracking-wider font-mono">pt</div>
                          </div>
                          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white/5 border border-white/10 transition-colors">
                            <i className="ri-arrow-right-s-line text-gray-500 text-base"></i>
                          </div>
                        </div>
                      </div>

                      {/* スコアバー（総合表示時のみ） */}
                      {artistSort === 'total' && (
                        <div className="mt-3 grid grid-cols-5 gap-1">
                          {scoreBars.map(bar => (
                            <Tooltip key={bar.label} content={bar.tooltip} subContent={bar.tooltipSub} position="top">
                              <div className="flex flex-col gap-1 w-full cursor-help">
                                <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full ${bar.color} rounded-full transition-all`}
                                    style={{ width: `${Math.min(((artist[bar.key] as number) / 20) * 100, 100)}%` }}
                                  ></div>
                                </div>
                                <div className="flex items-center justify-center gap-1">
                                  <span className="text-[9px] text-gray-600 font-mono text-center">{bar.label}</span>
                                  <span className="text-[9px] font-mono font-bold text-gray-500">
                                    {((artist[bar.key] as number) || 0).toFixed(1)}
                                  </span>
                                </div>
                              </div>
                            </Tooltip>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* NODES タブ */}
        {!loading && activeTab === 'nodes' && (
          <div className="space-y-6">
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <i className="ri-flashlight-line text-neon-pink"></i>
                <span className="text-sm font-bold tracking-wider text-gray-400 font-mono">
                  DAILY HEAT — 直近24時間のNFCタップ数
                </span>
              </div>
            </div>

            {nodes.length === 0 ? (
              <EmptyState message="NO DATA FOUND" />
            ) : (
              <div className="space-y-3">
                {nodes.map((node, index) => {
                  const rank = index + 1;
                  return (
                    <div
                      key={node.node_id}
                      onClick={() => navigate(`/shop/${node.node_id}`)}
                      className={`bg-white/5 border ${getRankBorder(rank)} rounded-lg p-4 hover:bg-white/10 transition-all ${getRankGlow(rank)} cursor-pointer`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${getRankColor(rank)} flex items-center justify-center flex-shrink-0`}>
                          <span className="text-2xl font-mono font-bold text-white">{rank}</span>
                        </div>
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-neon-pink to-neon-purple flex items-center justify-center flex-shrink-0">
                          <i className="ri-store-3-fill text-2xl text-white"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold tracking-wider truncate">{node.node_name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <i className="ri-flashlight-fill text-neon-pink text-xs"></i>
                            <span className="text-xs text-gray-400 font-mono">CRASH COUNT</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="text-right">
                            <div className="text-2xl font-mono font-bold text-neon-pink">
                              {node.crash_count.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-400 tracking-wider font-mono">CRASH</div>
                          </div>
                          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white/5 border border-white/10 transition-colors">
                            <i className="ri-arrow-right-s-line text-gray-500 text-base"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* SEEKERS タブ */}
        {!loading && activeTab === 'seekers' && (
          <div className="space-y-6">
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <i className="ri-footprint-line text-neon-green"></i>
                <span className="text-sm font-bold tracking-wider text-gray-400 font-mono">
                  FIELD COVERAGE — 現場踏破数（チェックイン数）
                </span>
              </div>
            </div>

            {seekers.length === 0 ? (
              <EmptyState message="NO DATA FOUND" />
            ) : (
              <div className="space-y-3">
                {seekers.map((seeker, index) => {
                  const rank = index + 1;
                  return (
                    <div
                      key={seeker.seeker_id}
                      onClick={() => navigate(`/artist/u/${seeker.seeker_id}`)}
                      className={`bg-white/5 border ${getRankBorder(rank)} rounded-lg p-4 hover:bg-white/10 transition-all ${getRankGlow(rank)} cursor-pointer`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${getRankColor(rank)} flex items-center justify-center flex-shrink-0`}>
                          <span className="text-2xl font-mono font-bold text-white">{rank}</span>
                        </div>
                        <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white/20 flex-shrink-0">
                          {seeker.avatar_url ? (
                            <img src={seeker.avatar_url} alt={seeker.seeker_name} className="w-full h-full object-cover object-top" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-neon-green to-neon-cyan flex items-center justify-center">
                              <i className="ri-user-line text-2xl text-white"></i>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-bold tracking-wider truncate">{seeker.seeker_name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <i className="ri-footprint-fill text-neon-green text-xs"></i>
                            <span className="text-xs text-gray-400 font-mono">CHECK-IN COUNT</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="text-right">
                            <div className="text-2xl font-mono font-bold text-neon-green">
                              {seeker.checkin_count.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-400 tracking-wider font-mono">SPOTS</div>
                          </div>
                          <div className="w-7 h-7 flex items-center justify-center rounded-full bg-white/5 border border-white/10 transition-colors">
                            <i className="ri-arrow-right-s-line text-gray-500 text-base"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
